import React, { useState, useEffect, useRef } from 'react';
import { Bell, Check, Info, AlertTriangle, CheckCircle2, RotateCcw } from 'lucide-react';
import { apiClient } from '../../services/apiClient';

interface AppNotification {
    id: number;
    title: string;
    message: string;
    type: 'info' | 'success' | 'warning' | 'error';
    link?: string;
    is_read: boolean;
    created_at: string;
}

const NotificationCenter: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState<AppNotification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const loadNotifications = async () => {
        setLoading(true);
        try {
            const res = await apiClient.get('/notifications?limit=20');
            setNotifications(res.notifications || []);
        } catch (err) {
            console.error('Failed to load notifications', err);
        }
        setLoading(false);
    };

    const loadUnreadCount = async () => {
        try {
            const res = await apiClient.get('/notifications/unread-count');
            setUnreadCount(res.count || 0);
        } catch (err) {
            console.error('Failed to load unread count', err);
        }
    };

    const handleOpen = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            loadNotifications();
        }
    };

    const markAsRead = async (id: number) => {
        try {
            await apiClient.post(`/notifications/${id}/read`, {});
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (err) {
            console.error('Mark read error:', err);
        }
    };

    const markAllAsRead = async () => {
        try {
            await apiClient.post('/notifications/read-all', {});
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);
        } catch (err) {
            console.error('Mark all read error:', err);
        }
    };

    // Poll unread count every 60s
    useEffect(() => {
        loadUnreadCount();
        const interval = setInterval(loadUnreadCount, 60000);
        return () => clearInterval(interval);
    }, []);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getIcon = (type: string) => {
        switch (type) {
            case 'success': return <CheckCircle2 size={16} className="text-green-500" />;
            case 'warning': return <AlertTriangle size={16} className="text-amber-500" />;
            case 'error': return <AlertTriangle size={16} className="text-red-500" />;
            default: return <Info size={16} className="text-blue-500" />;
        }
    };

    const formatTime = (dateStr: string) => {
        const d = new Date(dateStr);
        const now = new Date();
        const diffMs = now.getTime() - d.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
        return d.toLocaleDateString();
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={handleOpen}
                className="relative p-2 text-secondary hover:text-primary focus:outline-none transition-colors rounded-full hover:bg-hover"
            >
                <Bell size={20} />
                {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-red-500 border-2 border-white rounded-full">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-card rounded-xl shadow-xl border border-inner overflow-hidden z-50">
                    <div className="flex items-center justify-between px-4 py-3 bg-root border-b border-inner">
                        <h3 className="text-sm font-semibold text-primary">Notifications</h3>
                        {unreadCount > 0 && (
                            <button
                                onClick={markAllAsRead}
                                className="text-xs font-medium text-indigo hover:text-indigo-hover flex items-center gap-1"
                            >
                                <Check size={12} /> Mark all ready
                            </button>
                        )}
                    </div>

                    <div className="max-h-96 overflow-y-auto">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-8 text-muted">
                                <RotateCcw className="animate-spin mb-2" size={20} />
                                <span className="text-xs">Loading...</span>
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                                <div className="w-12 h-12 bg-input rounded-full flex items-center justify-center mb-3">
                                    <Bell size={20} className="text-muted" />
                                </div>
                                <p className="text-secondary text-sm">You have no new notifications.</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-inner">
                                {notifications.map(notif => (
                                    <div
                                        key={notif.id}
                                        className={`flex gap-3 p-4 hover:bg-hover transition-colors ${!notif.is_read ? 'bg-indigo/10' : ''}`}
                                        onClick={() => !notif.is_read && markAsRead(notif.id)}
                                    >
                                        <div className="mt-0.5">{getIcon(notif.type)}</div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm ${!notif.is_read ? 'font-semibold text-primary' : 'text-secondary'}`}>
                                                {notif.title}
                                            </p>
                                            <p className="text-xs text-muted mt-1 line-clamp-2">{notif.message}</p>
                                            <div className="flex items-center gap-3 mt-2">
                                                <span className="text-[10px] text-muted font-medium">{formatTime(notif.created_at)}</span>
                                                {notif.link && (
                                                    <a href={notif.link} className="text-[11px] font-medium text-indigo hover:text-indigo-hover">
                                                        View details →
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                        {!notif.is_read && (
                                            <button
                                                className="w-2 h-2 mt-1.5 rounded-full bg-indigo-500 flex-shrink-0"
                                                onClick={(e) => { e.stopPropagation(); markAsRead(notif.id); }}
                                                title="Mark as read"
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    {notifications.length > 0 && (
                        <div className="p-2 border-t border-inner bg-root text-center">
                            <button
                                onClick={() => { setIsOpen(false); loadNotifications(); }}
                                className="text-xs font-medium text-secondary hover:text-primary w-full rounded-md py-1"
                            >
                                View all cleared
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationCenter;
